export class LocalStorer<T extends object & { unshift?: never }> {
    readonly storageKey: string;
    private readonly default: T;
    private cache?: T;

    private readonly onChangeCallbacks: (() => void)[] = [];

    constructor(opts: { storageKey: string; defaultData: T; memCache?: boolean }) {
        this.storageKey = opts.storageKey;
        this.default = opts.defaultData;
        if (opts.memCache) this.cache = this.loadFromStorage();
    }

    protected loadFromStorage(): T {
        const data = window.localStorage.getItem(this.storageKey);
        if (data) return JSON.parse(data) as T;
        return this.default;
    }

    protected saveToStorage(data: T): void {
        window.localStorage.setItem(this.storageKey, JSON.stringify(data));
    }

    /**
     * Get data entry.
     * @param key
     * @returns undefined if data was not loaded.
     */
    getEntry<K extends keyof T>(key: K): T[K] {
        const data = this.cache ?? this.loadFromStorage();
        if (data && (typeof data[key] !== "undefined" || typeof this.default[key] === "undefined")) return data[key];
        return this.default[key];
    }

    /**
     * Set data and save.
     * @param key
     * @param value
     */
    setEntry<K extends keyof T>(key: K, value: T[K]): void {
        const data = this.cache ?? this.loadFromStorage();
        data[key] = value;
        this.saveToStorage(data);
        for (const cb of this.onChangeCallbacks) {
            cb();
        }
    }

    addOnChange(cb: () => void): void {
        for (const f of this.onChangeCallbacks) {
            if (cb == f) throw new Error("Callback already registered!");
        }
        this.onChangeCallbacks.push(cb);
    }

    removeOnChange(cb: () => void): void {
        const idx = this.onChangeCallbacks.findIndex((v) => v == cb);
        if (idx !== -1) this.onChangeCallbacks.splice(idx, 1);
    }
}

type LocalApiStorerStored<T> = {
    timestamp: number;
    data: T;
};

export class LocalApiStorer<T> {
    readonly maxAge: number;
    private readonly storer: LocalStorer<LocalApiStorerStored<T>>;
    private readonly loadFunc: () => Promise<T>;
    private wasLoaded = false;
    private isLoadingPromise: Promise<T> | undefined;

    constructor(opts: {
        storageKey: string;
        maxAge: number;
        defaultData: T;
        loadFunc: () => Promise<T>;
        autoLoad: boolean;
    }) {
        this.storer = new LocalStorer<LocalApiStorerStored<T>>({
            storageKey: opts.storageKey,
            defaultData: {
                timestamp: -1,
                data: undefined as T,
            },
        });
        this.maxAge = opts.maxAge;
        this.loadFunc = opts.loadFunc;
        this.wasLoaded = this.checkStorage();
        if (!this.wasLoaded && opts.autoLoad) this.getData();
    }

    private checkStorage() {
        const timestamp = this.storer.getEntry("timestamp");
        return Date.now() < timestamp + this.maxAge;
    }

    /**
     * Get cached data or load from API.
     * @returns
     */
    async getData(): Promise<T> {
        if (this.wasLoaded) return this.storer.getEntry("data");
        if (!this.isLoadingPromise) {
            this.isLoadingPromise = new Promise((res, rej) => {
                this.loadFunc()
                    .then((data) => {
                        this.storer.setEntry("data", data);
                        this.storer.setEntry("timestamp", Date.now());
                        this.wasLoaded = true;
                        res(data);
                    })
                    .catch((err) => {
                        rej(err);
                    })
                    .finally(() => {
                        this.isLoadingPromise = undefined;
                    });
            });
        }
        return this.isLoadingPromise;
    }
}
